/**
 * Pure helpers that derive indexed Sentry tags from an axios-shaped API error.
 *
 * Kept free of Vue and Sentry imports so the module behaves identically in the
 * Vue 2 Jest harness and the Vue 3 production build, and can be imported from
 * both the common-lib plugin and the theme-level Sentry config.
 *
 * Tag values are normalized to keep cardinality low (Sentry tags are indexed):
 * the query string is dropped entirely (it may carry tokens/emails), the host
 * is excluded, and dynamic path segments are replaced with placeholders.
 */

// Sentry rejects tag values longer than 200 characters.
const SENTRY_TAG_VALUE_LIMIT = 200;

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;
const LONG_HEX_SEGMENT = /^[0-9a-f]{16,}$/i;
const EVM_ADDRESS_SEGMENT = /^0x[0-9a-f]{6,}$/i;
// Legacy base58 (1.../3...) and bech32 (bc1...) use different charsets.
const BTC_LEGACY_ADDRESS_SEGMENT = /^[13][a-km-zA-HJ-NP-Z1-9]{20,}$/;
const BTC_BECH32_ADDRESS_SEGMENT = /^bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{20,}$/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

// String-based trims: sonarjs/slow-regex forbids the /\/+$/-style equivalents.
const trimTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === '/') {
    end -= 1;
  }
  return value.slice(0, end);
};

const trimLeadingSlashes = (value: string): string => {
  let start = 0;
  while (start < value.length && value[start] === '/') {
    start += 1;
  }
  return value.slice(start);
};

const normalizeSegment = (segment: string): string => {
  if (segment === '') {
    return segment;
  }
  if (
    UUID_SEGMENT.test(segment) ||
    NUMERIC_SEGMENT.test(segment) ||
    LONG_HEX_SEGMENT.test(segment)
  ) {
    return ':id';
  }
  if (
    EVM_ADDRESS_SEGMENT.test(segment) ||
    BTC_LEGACY_ADDRESS_SEGMENT.test(segment) ||
    BTC_BECH32_ADDRESS_SEGMENT.test(segment)
  ) {
    return ':address';
  }
  return segment;
};

export const normalizeEndpoint = (rawUrl: string, baseURL?: string): string => {
  const isAbsolute = /^https?:\/\//i.test(rawUrl);
  const joined = isAbsolute || !baseURL
    ? rawUrl
    : `${trimTrailingSlashes(baseURL)}/${trimLeadingSlashes(rawUrl)}`;

  let pathname: string;
  try {
    // The dummy base makes relative URLs parseable; absolute URLs ignore it.
    pathname = new URL(joined, 'https://relative-url.invalid').pathname;
  } catch {
    pathname = joined.split('?')[0];
  }

  const normalized = pathname.split('/').map(normalizeSegment).join('/');

  return (normalized || '/').slice(0, SENTRY_TAG_VALUE_LIMIT);
};

const extractTagsFromAxiosShape = (error: unknown): Record<string, string> | null => {
  if (!isRecord(error) || !isRecord(error.config)) {
    return null;
  }

  const { url, baseURL, method } = error.config;

  if (typeof url !== 'string' || url === '') {
    return null;
  }

  const tags: Record<string, string> = {
    'api.endpoint': normalizeEndpoint(url, typeof baseURL === 'string' ? baseURL : undefined),
  };

  if (typeof method === 'string' && method !== '') {
    tags['api.method'] = method.toUpperCase();
  }

  const status = isRecord(error.response) ? error.response.status : undefined;
  if (typeof status === 'number') {
    tags['api.status'] = String(status);
  }

  return tags;
};

/**
 * Errors that wrap an API failure (e.g. TokenRefreshError) keep the original axios
 * error on `cause`, so the endpoint/method/status still have to be recoverable from
 * the wrapper. Deliberately one level deep only: no recursion, so a cyclic cause
 * chain can never hang the SDK.
 */
export const extractApiErrorTags = (error: unknown): Record<string, string> | null => {
  const direct = extractTagsFromAxiosShape(error);

  if (direct !== null) {
    return direct;
  }

  return isRecord(error) ? extractTagsFromAxiosShape(error.cause) : null;
};
